import React from 'react';

interface HistoricalSignal {
  id: number;
  direction: string;
  confidence: number;
  strike?: number;
  target?: number;
  stop_loss?: number;
  scores?: { total?: number };
  spot_price?: number;
  direction_changed?: boolean;
  previous_direction?: string;
  created_at: string;
}

interface Props {
  niftyHistory: HistoricalSignal[];
  bankniftyHistory: HistoricalSignal[];
}

const dirColor: Record<string, string> = {
  CE: '#26a65b',
  PE: '#e74c3c',
  WAIT: '#f39c12',
};

const dirIcon: Record<string, string> = {
  CE: '▲',
  PE: '▼',
  WAIT: '⏸',
};

function HistoryRow({ signals, label }: { signals: HistoricalSignal[]; label: string }) {
  if (!signals.length) return null;

  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ color: '#555', fontSize: '0.6rem', fontWeight: 700, marginBottom: 6, letterSpacing: 1 }}>
        {label} · LAST {signals.length} SIGNALS
      </div>
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none' }}>
        {signals.map((sig, i) => {
          const color = dirColor[sig.direction] || '#666';
          const isLatest = i === 0;
          return (
            <div
              key={sig.id}
              title={`${sig.direction} | Strike: ${sig.strike || 'N/A'} | Conf: ${sig.confidence}/10 | Score: ${sig.scores?.total ?? '?'}`}
              style={{
                flexShrink: 0,
                background: isLatest ? `${color}18` : '#0d0d14',
                border: `1px solid ${isLatest ? color : '#1a1a2a'}`,
                borderRadius: 6,
                padding: '6px 10px',
                minWidth: 76,
                position: 'relative',
              }}
            >
              {isLatest && (
                <div style={{
                  position: 'absolute', top: -6, right: 4,
                  background: color, color: '#000',
                  fontSize: '0.5rem', fontWeight: 800,
                  padding: '1px 5px', borderRadius: 6,
                }}>LATEST</div>
              )}
              {sig.direction_changed && !isLatest && (
                <div style={{
                  position: 'absolute', top: -6, right: 4,
                  background: '#f39c12', color: '#000',
                  fontSize: '0.5rem', fontWeight: 700,
                  padding: '1px 5px', borderRadius: 6,
                }}>CHANGED</div>
              )}
              <div style={{ color, fontSize: '0.75rem', fontWeight: 800 }}>
                {dirIcon[sig.direction]} {sig.direction}
              </div>
              <div style={{ color: '#888', fontSize: '0.6rem', marginTop: 2 }}>
                Conf {sig.confidence}/10
              </div>
              {sig.strike && (
                <div style={{ color: '#666', fontSize: '0.58rem' }}>
                  @ {sig.strike.toLocaleString('en-IN')}
                </div>
              )}
              <div style={{ color: '#444', fontSize: '0.55rem', marginTop: 3 }}>
                {fmtTime(sig.created_at)}
              </div>
              {/* Confidence bar */}
              <div style={{ marginTop: 3, height: 2, background: '#1a1a2a', borderRadius: 1 }}>
                <div style={{ width: `${sig.confidence * 10}%`, height: '100%', background: color, borderRadius: 1 }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SignalHistory({ niftyHistory, bankniftyHistory }: Props) {
  if (!niftyHistory.length && !bankniftyHistory.length) return null;

  // Compute win/loss stats from history
  const stats = (history: HistoricalSignal[]) => {
    const total = history.length;
    const ce = history.filter(s => s.direction === 'CE').length;
    const pe = history.filter(s => s.direction === 'PE').length;
    const wait = history.filter(s => s.direction === 'WAIT').length;
    const avgConf = total ? Math.round(history.reduce((a, s) => a + s.confidence, 0) / total) : 0;
    return { total, ce, pe, wait, avgConf };
  };
  const ns = stats(niftyHistory);
  const bs = stats(bankniftyHistory);

  return (
    <div style={{ background: '#0d0d14', border: '1px solid #1a1a2a', borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ color: '#555', fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1 }}>SIGNAL HISTORY</div>
        <div style={{ display: 'flex', gap: 16 }}>
          {[{ label: 'NIFTY', s: ns, color: '#4fc3f7' }, { label: 'BANKNIFTY', s: bs, color: '#ce93d8' }].map(({ label, s, color }) => (
            <div key={label} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ color, fontSize: '0.6rem', fontWeight: 700 }}>{label}</span>
              <span style={{ color: '#26a65b', fontSize: '0.6rem' }}>CE×{s.ce}</span>
              <span style={{ color: '#e74c3c', fontSize: '0.6rem' }}>PE×{s.pe}</span>
              <span style={{ color: '#f39c12', fontSize: '0.6rem' }}>WAIT×{s.wait}</span>
              <span style={{ color: '#555', fontSize: '0.6rem' }}>Avg conf {s.avgConf}/10</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <HistoryRow signals={niftyHistory} label="NIFTY" />
        <HistoryRow signals={bankniftyHistory} label="BANKNIFTY" />
      </div>
    </div>
  );
}
